import { isIntakeFieldValue } from './fieldPersistence';
import type { GeneratedIntakeField, IntakeFieldSuggestion, IntakeFieldValue } from './types';

export const REPEAT_RECORDS_KEY = '_repeat_records';

export interface IntakeRepeatRecord {
  record_id: string;
  order: number;
  values: Record<string, IntakeFieldValue>;
  /**
   * The document this record was opened by, owned by the server. It must be
   * echoed back on every record we PUT: the manual-evidence validator strips
   * whatever a client sends for this key and re-attaches the stored link, so a
   * dropped link silently unlinks the record and the next upload matches it by
   * the legacy heuristic instead of by ownership.
   */
  document_reference_id?: string;
}

export interface IntakeRepeatGroupState {
  acknowledgedRecords: IntakeRepeatRecord[];
  draftRecords: IntakeRepeatRecord[];
}

export type IntakeRepeatGroupStates = Record<string, IntakeRepeatGroupState>;

export function repeatRecordsEqual(
  left: IntakeRepeatRecord[],
  right: IntakeRepeatRecord[],
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function reconcileRepeatGroupStates(
  serverRecordsByGroup: Record<string, IntakeRepeatRecord[]>,
  previous: IntakeRepeatGroupStates,
): IntakeRepeatGroupStates {
  return Object.fromEntries(
    Object.entries(serverRecordsByGroup).map(([groupKey, serverRecords]) => {
      const existing = previous[groupKey];
      if (!existing) {
        return [groupKey, { acknowledgedRecords: serverRecords, draftRecords: serverRecords }];
      }
      const draftIsDirty = !repeatRecordsEqual(existing.draftRecords, existing.acknowledgedRecords);
      return [
        groupKey,
        {
          acknowledgedRecords: serverRecords,
          draftRecords: draftIsDirty ? existing.draftRecords : serverRecords,
        },
      ];
    }),
  );
}

export function repeatGroupKey(field: GeneratedIntakeField): string {
  return `${field.view}:${field.requirement_key ?? ''}:${field.group_id}`;
}

function legacyRecordId(requirementKey: string): string {
  const slug = requirementKey
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `legacy-${slug || 'record'}`;
}

function normalizedStoredRecords(value: unknown): IntakeRepeatRecord[] {
  if (!Array.isArray(value)) return [];
  return value
    .flatMap((entry, index) => {
      if (!entry || typeof entry !== 'object') return [];
      const raw = entry as Record<string, unknown>;
      if (typeof raw.record_id !== 'string' || !raw.record_id) return [];
      if (!raw.values || typeof raw.values !== 'object' || Array.isArray(raw.values)) return [];
      const values = Object.fromEntries(
        Object.entries(raw.values).filter((pair): pair is [string, IntakeFieldValue] =>
          isIntakeFieldValue(pair[1]),
        ),
      );
      const documentReferenceId =
        typeof raw.document_reference_id === 'string' && raw.document_reference_id
          ? raw.document_reference_id
          : undefined;
      return [
        {
          record_id: raw.record_id,
          order: Number(raw.order) || index,
          values,
          ...(documentReferenceId ? { document_reference_id: documentReferenceId } : {}),
        },
      ];
    })
    .sort((left, right) => left.order - right.order)
    .map((record, order) => ({ ...record, order }));
}

export function hydrateRepeatRecords(
  extractedFields: Record<string, unknown>,
  fieldKeys: string[],
  requirementKey: string,
): IntakeRepeatRecord[] {
  const stored = normalizedStoredRecords(extractedFields[REPEAT_RECORDS_KEY]);
  if (stored.length > 0) return stored;

  const values = Object.fromEntries(
    fieldKeys.flatMap(fieldKey => {
      const value = extractedFields[fieldKey];
      return isIntakeFieldValue(value) ? [[fieldKey, value] as const] : [];
    }),
  );
  return [{ record_id: legacyRecordId(requirementKey), order: 0, values }];
}

export function addRepeatRecord(
  records: IntakeRepeatRecord[],
  recordId: string,
): IntakeRepeatRecord[] {
  return [...records, { record_id: recordId, order: records.length, values: {} }];
}

export function updateRepeatRecord(
  records: IntakeRepeatRecord[],
  recordId: string,
  fieldKey: string,
  value: IntakeFieldValue,
): IntakeRepeatRecord[] {
  return records.map(record =>
    record.record_id === recordId
      ? { ...record, values: { ...record.values, [fieldKey]: value } }
      : record,
  );
}

export function removeRepeatRecord(
  records: IntakeRepeatRecord[],
  recordId: string,
): IntakeRepeatRecord[] {
  if (records[0]?.record_id === recordId) return records;
  return records
    .filter(record => record.record_id !== recordId)
    .map((record, order) => ({ ...record, order }));
}

export function repeatRecordsEvidence(records: IntakeRepeatRecord[]): Record<string, unknown> {
  return {
    [REPEAT_RECORDS_KEY]: records,
    ...(records[0]?.values ?? {}),
  };
}

export function duplicateRepeatRecordError(
  records: IntakeRepeatRecord[],
  identityFieldKey: string,
  message = 'Each value can only be added once.',
): string | undefined {
  const seen = new Set<string>();
  for (const record of records) {
    const value = record.values[identityFieldKey];
    if (typeof value !== 'string' || !value.trim()) continue;
    const normalized = value.trim().toLowerCase();
    if (seen.has(normalized)) return message;
    seen.add(normalized);
  }
  return undefined;
}

export interface RepeatDuplicatePolicy {
  fieldKey: string;
  message: string;
}

export function repeatDuplicatePolicy(
  fields: GeneratedIntakeField[],
): RepeatDuplicatePolicy | undefined {
  const scope = fields[0]?.repeat_scope?.toLowerCase();
  if (scope === 'per-state') {
    const stateField = fields.find(field => field.destination.field_key.includes('state'));
    return stateField
      ? {
          fieldKey: stateField.destination.field_key,
          message: 'Each state can only be added once.',
        }
      : undefined;
  }
  if (scope === 'per-registration') {
    const registrationField = fields.find(field =>
      /number|registration/.test(field.destination.field_key),
    );
    return registrationField
      ? {
          fieldKey: registrationField.destination.field_key,
          message: 'Each registration number can only be added once.',
        }
      : undefined;
  }
  return undefined;
}

/**
 * The key a suggestion is indexed under.
 *
 * A repeated field takes one suggestion *per record*: a provider who uploads a
 * CA and an NY licence gets two `license_state` suggestions, one per record,
 * and keying by `field_id` alone would let them collide and cancel each other
 * out. Suggestions with no record (every non-repeated field) keep the bare
 * `field_id`, so callers that read the map by field id are unaffected.
 */
export function suggestionEntryKey(fieldId: string, repeatRecordId?: string | null): string {
  return repeatRecordId ? `${fieldId}::${repeatRecordId}` : fieldId;
}

/**
 * The record a suggestion targets, or `undefined` when it cannot be placed.
 *
 * A suggestion may name a record that does not exist yet: the server creates a
 * record lazily, on the first accepted suggestion for a document, under an id
 * it derives from that document. Those ids resolve here so the group can offer
 * the suggestion on a placeholder entry — the alternative is dropping every
 * suggestion from a provider's second uploaded licence, which is the headline
 * case. Practice address (`primaryOnly`) keeps its primary-record-only rule.
 */
export function repeatRecordIdForSuggestion(
  records: IntakeRepeatRecord[],
  _fieldKey: string,
  suggestion: IntakeFieldSuggestion,
  primaryOnly = false,
): string | undefined {
  const recordId = suggestion.repeat_record_id;
  if (!recordId) return undefined;
  if (primaryOnly) return records[0]?.record_id === recordId ? recordId : undefined;
  const matches = records.filter(record => record.record_id === recordId).length;
  // 0 → a record the server has not created yet; 1 → an existing record;
  // more than one → ambiguous, drop it.
  return matches <= 1 ? recordId : undefined;
}

/**
 * Record ids named by suggestions that no record holds yet, in first-seen
 * order. These render as placeholder entries; they are never added to draft
 * state or persisted, because the server owns their creation.
 */
export function pendingSuggestionRecordIds(
  records: IntakeRepeatRecord[],
  suggestionRecordIds: readonly (string | undefined)[],
): string[] {
  const existing = new Set(records.map(record => record.record_id));
  const pending: string[] = [];
  for (const recordId of suggestionRecordIds) {
    if (!recordId || existing.has(recordId) || pending.includes(recordId)) continue;
    pending.push(recordId);
  }
  return pending;
}
